<template>
  <div class="rounded-lg shadow bg-white p-4">
    <template v-if="!isDetail">
      <div class="flex flex-wrap justify-between items-center gap-2 mb-4">
        <h1 class="font-bold text-xl mb-0">알림톡 발송 이력</h1>
      </div>

      <div class="flex flex-wrap items-end justify-start gap-2 mb-4">
        <div class="min-w-[160px]">
          <label class="block text-sm font-medium text-gray-600 mb-1">검색 시작일</label>
          <b-form-input v-model="startDate" type="date" />
        </div>
        <div class="min-w-[160px]">
          <label class="block text-sm font-medium text-gray-600 mb-1">검색 종료일</label>
          <b-form-input v-model="endDate" type="date" />
        </div>
        <div class="min-w-[260px]">
          <label class="block text-sm font-medium text-gray-600 mb-1">템플릿</label>
          <b-form-select v-model="requestTemplateCode">
            <option value="">전체 템플릿</option>
            <option v-for="template in templates" :key="template.id" :value="template.templateCode">
              {{ template.templateName }} ({{ template.templateCode }})
            </option>
          </b-form-select>
        </div>
        <button class="btn btn-primary" @click="searchRequests">검색</button>
      </div>
      <div class="table-responsive">
        <table class="table table-hover align-middle">
          <thead>
            <tr>
              <th>요청 일시</th>
              <th>발송 요청자</th>
              <th>템플릿</th>
              <th>전체</th>
              <th>접수 결과</th>
              <th>전달 결과</th>
              <th>상태</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="request in requests" :key="request.id">
              <td>{{ formatDate(request.requestedAt) }}</td>
              <td>{{ request.requestedByName || request.requestedByEmail || '-' }}</td>
              <td>{{ request.templateName || request.templateCode }}</td>
              <td>{{ request.totalCount }}</td>
              <td>
                <div class="flex flex-col items-start gap-1">
                  <span class="badge bg-success">성공 {{ request.acceptedCount }}</span>
                  <span class="badge bg-danger">실패 {{ request.rejectedCount }}</span>
                </div>
              </td>
              <td>
                <div class="flex flex-col items-start gap-1">
                  <span class="badge bg-success">성공 {{ request.deliveredCount }}</span>
                  <span class="badge bg-danger">실패 {{ request.failedCount }}</span>
                  <span class="badge bg-secondary">대기 중 {{ request.pendingCount }}</span>
                </div>
              </td>
              <td><span class="badge" :class="statusClass(request.status)">{{ statusLabel(request.status) }}</span></td>
              <td><button class="btn btn-sm btn-outline-primary" @click="openDetail(request.id)">상세</button></td>
            </tr>
            <tr v-if="requests.length === 0">
              <td colspan="8" class="text-center text-gray-500 py-5">발송 이력이 없습니다.</td>
            </tr>
          </tbody>
        </table>
      </div>
      <b-pagination
        align="center"
        v-model="requestPage"
        :total-rows="requestTotal"
        :per-page="requestPageSize"
        @page-click="changeRequestPage"
      />
    </template>

    <template v-else>
      <div class="flex flex-wrap justify-between items-center gap-2 mb-4">
        <div class="flex items-center gap-2">
          <button class="btn btn-outline-secondary" @click="goList">목록</button>
          <h1 class="font-bold text-xl mb-0">알림톡 발송 결과 #{{ requestId }}</h1>
        </div>
        <div class="flex flex-wrap gap-2">
          <button class="btn btn-primary" :disabled="syncing" @click="syncResult">
            <span v-if="syncing" class="spinner-border spinner-border-sm mr-1"></span>
            결과 동기화
          </button>
          <button class="btn btn-success" :disabled="excelDownloading" @click="downloadRecipientsExcel">
            <span v-if="excelDownloading" class="spinner-border spinner-border-sm mr-1"></span>
            엑셀 다운로드
          </button>
        </div>
      </div>

      <div v-if="request" class="mb-4">
        <div class="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
          <div class="border rounded p-3"><div class="text-sm text-gray-500">전체</div><b>{{ request.totalCount }}</b></div>
          <div class="border rounded p-3">
            <div class="text-sm text-gray-500 mb-2">접수 결과</div>
            <span class="text-success font-semibold mr-3">성공 {{ request.acceptedCount }}</span>
            <span class="text-danger font-semibold">실패 {{ request.rejectedCount }}</span>
          </div>
          <div class="border rounded p-3">
            <div class="text-sm text-gray-500 mb-2">전달 결과</div>
            <span class="text-success font-semibold mr-3">성공 {{ request.deliveredCount }}</span>
            <span class="text-danger font-semibold mr-3">실패 {{ request.failedCount }}</span>
            <span class="font-semibold">대기 중 {{ request.pendingCount }}</span>
          </div>
          <div class="border rounded p-3"><div class="text-sm text-gray-500">상태</div><b>{{ statusLabel(request.status) }}</b></div>
        </div>
        <div class="text-sm text-gray-600">
          요청 {{ formatDate(request.requestedAt) }} · 요청자 {{ request.requestedByName || request.requestedByEmail || '-' }} ·
          템플릿 {{ request.templateName || request.templateCode }} · 마지막 동기화 {{ formatDate(request.lastSyncedAt) }}
        </div>
        <div v-if="request.syncErrorMessage" class="alert alert-warning mt-3 mb-0">{{ request.syncErrorMessage }}</div>
      </div>

      <div class="flex flex-wrap gap-2 mb-3">
        <input
          v-model="recipientPhoneNumber"
          class="form-control max-w-[320px]"
          placeholder="전화번호 검색"
          @keyup.enter="searchRecipients"
        >
        <button class="btn btn-outline-primary" @click="searchRecipients">검색</button>
      </div>

      <div class="table-responsive">
        <table class="table table-hover align-middle">
          <thead>
            <tr>
              <th>전화번호</th>
              <th>접수 상태</th>
              <th>NHN 요청 ID</th>
              <th>요청 시각</th>
              <th>수신/확인 시각</th>
              <th>전달 결과</th>
              <th>결과 코드</th>
              <th>사유</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="recipient in recipients" :key="recipient.id">
              <td>{{ formatPhone(recipient.phoneNumber) }}</td>
              <td>
                <span class="badge" :class="requestStatusClass(recipient.requestStatus)">
                  {{ requestStatusLabel(recipient.requestStatus) }}
                </span>
              </td>
              <td class="text-xs">{{ recipient.nhnRequestId || '-' }}</td>
              <td>{{ formatDate(recipient.nhnRequestDate || recipient.acceptedAt) }}</td>
              <td>{{ formatDate(recipient.receiveDate || recipient.deliveryLastSyncedAt) }}</td>
              <td><span class="badge" :class="deliveryClass(recipient.deliveryStatus)">{{ deliveryLabel(recipient.deliveryStatus) }}</span></td>
              <td>{{ recipient.resultCode || '-' }}</td>
              <td>{{ recipient.resultCodeName || recipient.exceptionMessage || '-' }}</td>
            </tr>
            <tr v-if="recipients.length === 0">
              <td colspan="8" class="text-center text-gray-500 py-5">검색 결과가 없습니다.</td>
            </tr>
          </tbody>
        </table>
      </div>
      <b-pagination
        align="center"
        v-model="recipientPage"
        :total-rows="recipientTotal"
        :per-page="recipientPageSize"
        @page-click="changeRecipientPage"
      />
    </template>
  </div>
</template>

<script>
import moment from "moment";
import api from "@/api/alimtalkSendRequest";
import alimtalkTemplateApi from "@/api/alimtalkTemplate";

export default {
  name: "AlimtalkSendRequests",
  data() {
    return {
      requests: [], requestPage: 1, requestPageSize: 20, requestTotal: 0,
      request: null, recipients: [], recipientPage: 1, recipientPageSize: 50, recipientTotal: 0,
      recipientPhoneNumber: '', syncing: false,
      startDate: '', endDate: '', requestTemplateCode: '',
      templates: [], excelDownloading: false,
    }
  },
  computed: {
    requestId() { return this.$route.params.id ? Number(this.$route.params.id) : null },
    isDetail() { return !!this.requestId },
  },
  watch: {
    '$route.params.id'() { this.loadPage() },
  },
  mounted() { this.loadTemplates(); this.loadPage() },
  methods: {
    loadPage() { if (this.isDetail) this.loadDetail(); else this.loadRequests() },
    loadTemplates() {
      alimtalkTemplateApi.list({}, (response) => {
        this.templates = response.data.data || []
      }, this.showError)
    },
    requestFilterParams() {
      const params = {}
      if (this.startDate) params.startDate = this.startDate
      if (this.endDate) params.endDate = this.endDate
      if (this.requestTemplateCode) params.templateCode = this.requestTemplateCode
      return params
    },
    validatePeriod() {
      if (this.startDate && this.endDate && this.startDate > this.endDate) {
        this.$toast.error('검색 시작일은 종료일보다 늦을 수 없습니다.', {position: 'top-right'})
        return false
      }
      return true
    },
    loadRequests() {
      api.list({
        ...this.requestFilterParams(),
        page: this.requestPage,
        size: this.requestPageSize,
      }, (response) => {
        const page = response.data.data
        this.requests = page.content || []
        this.requestTotal = page.totalElements || 0
      }, this.showError)
    },
    loadDetail() {
      api.detail(this.requestId, (response) => { this.request = response.data.data }, this.showError)
      this.loadRecipients()
    },
    loadRecipients() {
      api.recipients(this.requestId, {
        phoneNumber: this.recipientPhoneNumber,
        page: this.recipientPage,
        size: this.recipientPageSize,
      }, (response) => {
        const page = response.data.data
        this.recipients = page.content || []
        this.recipientTotal = page.totalElements || 0
      }, this.showError)
    },
    syncResult() {
      this.syncing = true
      api.sync(this.requestId, (response) => {
        this.syncing = false
        this.request = response.data.data
        this.loadRecipients()
        this.$toast.show('발송 결과를 동기화했습니다.', {position: 'top-right', duration: 1500})
      }, (error) => { this.syncing = false; this.showError(error) })
    },
    searchRecipients() { this.recipientPage = 1; this.loadRecipients() },
    searchRequests() {
      if (!this.validatePeriod()) return
      this.requestPage = 1
      this.loadRequests()
    },
    downloadRecipientsExcel() {
      this.excelDownloading = true
      api.exportRecipients(this.requestId, {phoneNumber: this.recipientPhoneNumber}, (response) => {
        const url = window.URL.createObjectURL(new Blob([response.data], {type: response.headers['content-type']}))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `알림톡_발송_결과_${this.requestId}_${moment().format('YYYYMMDD_HHmmss')}.xlsx`)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
        this.excelDownloading = false
      }, (error) => {
        this.excelDownloading = false
        this.showError(error)
      })
    },
    changeRequestPage(event, page) { this.requestPage = page; this.loadRequests() },
    changeRecipientPage(event, page) { this.recipientPage = page; this.loadRecipients() },
    openDetail(id) { this.$router.push({name: 'AlimtalkSendRequestDetail', params: {id}}) },
    goList() { this.$router.push({name: 'AlimtalkSendRequests'}) },
    formatDate(value) { return value ? moment(value).format('YYYY-MM-DD HH:mm:ss') : '-' },
    formatPhone(value) {
      const digits = (value || '').replace(/\D/g, '')
      return digits.length === 11 ? digits.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3') : value || '-'
    },
    statusLabel(status) {
      return {REQUESTING: '요청 중', PROCESSING: '전달 결과 대기 중', COMPLETED: '완료', COMPLETED_WITH_FAILURES: '일부 실패', FAILED: '접수 실패'}[status] || status || '-'
    },
    statusClass(status) {
      return status === 'COMPLETED' ? 'bg-success' : status === 'FAILED' ? 'bg-danger' : status === 'COMPLETED_WITH_FAILURES' ? 'bg-warning text-dark' : 'bg-secondary'
    },
    deliveryLabel(status) {
      return {PENDING: '확인 중', DELIVERED: '성공', FAILED: '실패', CANCELED: '취소'}[status] || '접수 실패'
    },
    requestStatusLabel(status) {
      return {COMPLETE: '접수 성공', FAILED: '접수 실패', WAITING: '접수 중'}[status] || '접수 중'
    },
    requestStatusClass(status) {
      return status === 'COMPLETE' ? 'bg-success' : status === 'FAILED' ? 'bg-danger' : 'bg-secondary'
    },
    deliveryClass(status) {
      return status === 'DELIVERED' ? 'bg-success' : status === 'FAILED' || status === 'CANCELED' ? 'bg-danger' : 'bg-secondary'
    },
    showError(error) {
      this.$toast.error(error?.response?.data?.message || error?.message || '요청 처리 중 오류가 발생했습니다.', {position: 'top-right'})
    },
  },
}
</script>
